import { set } from "@vueuse/core";
import { ArkErrors, type } from "arktype";
import { ref, toValue, watch } from "vue";
import { useSlideDatasetFull } from "../../services/use.configurator-slide.js";
import { type Config, DateStringArraySchema, NumericStringArraySchema } from "../domain/config.type.js";

function hasDatasetAndIsDifferentTo(dataset: DOMStringMap, oldValue: Config, key: keyof Config) {
	return dataset[key] && dataset[key] !== oldValue[key];
}

export function useConfiguration() {
	const config = ref<Config>({
		enabledDays: [0, 1, 2, 3, 4, 5, 6, 7],
		disabledDates: [],
		forceEnabledDates: [],
		todayOpen: false,
		tomorrowOpen: true,
		daysInAdvance: 14,
		labelTemplateSelector: "#label-template",
	});
	const { dataset } = useSlideDatasetFull();

	function assignDataset(_dataset: DOMStringMap | undefined) {
		if (_dataset === undefined) {
			return;
		}
		const currentConfig = config.value;
		const currHasDatasetAndIsDifferentTo = (key: keyof Config) => hasDatasetAndIsDifferentTo(_dataset, currentConfig, key);

		const partialConfig: Partial<Config> = {};

		if (currHasDatasetAndIsDifferentTo("enabledDays")) {
			const val = NumericStringArraySchema(JSON.parse(_dataset.enabledDays ?? "{}"));
			if (!(val instanceof ArkErrors)) {
				partialConfig.enabledDays = val;
			}
		}

		if (currHasDatasetAndIsDifferentTo("disabledDates")) {
			const val = DateStringArraySchema(JSON.parse(_dataset.disabledDates ?? "{}"));
			if (!(val instanceof ArkErrors)) {
				partialConfig.disabledDates = val;
			}
		}

		if (currHasDatasetAndIsDifferentTo("forceEnabledDates")) {
			const val = DateStringArraySchema(JSON.parse(_dataset.forceEnabledDates ?? "{}"));
			if (!(val instanceof ArkErrors)) {
				partialConfig.forceEnabledDates = val;
			}
		}

		if (currHasDatasetAndIsDifferentTo("todayOpen")) {
			const val = type("boolean")(JSON.parse(_dataset.todayOpen ?? "{}"));
			if (!(val instanceof ArkErrors)) {
				partialConfig.todayOpen = val;
			}
		}
		if (currHasDatasetAndIsDifferentTo("tomorrowOpen")) {
			const val = type("boolean")(JSON.parse(_dataset.tomorrowOpen ?? "{}"));
			if (!(val instanceof ArkErrors)) {
				partialConfig.tomorrowOpen = val;
			}
		}

		if (currHasDatasetAndIsDifferentTo("daysInAdvance")) {
			const val = type("string.numeric.parse")(_dataset.daysInAdvance);
			if (!(val instanceof ArkErrors)) {
				partialConfig.daysInAdvance = val;
			}
		}

		if (currHasDatasetAndIsDifferentTo("labelTemplateSelector")) {
			const val = type("string")(_dataset.labelTemplateSelector);
			if (!(val instanceof ArkErrors)) {
				partialConfig.labelTemplateSelector = val;
			}
		}

		set(config, {
			...config.value,
			...partialConfig,
		});
	}

	watch(dataset, (_dataset) => {
		assignDataset(_dataset);
	});

	assignDataset(toValue(dataset));

	return { config };
}
